var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import DOMPurification from 'dompurify';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/Button/Button';
import { Checkbox } from '@/components/Checkbox/Checkbox';
import { Input } from '@/components/Input/Input';
import { LanguageSelect } from '@/components/LanguageSelect/LanguageSelect';
import { languageByCountryCode } from '@/config/constants';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useHandleLinksManually } from '@/hooks/useHandleLinksManually';
import { useSocket } from '@/hooks/useSocket';
import { visitorInformationSelector, themeSelector, isMfaSelector, variantSelector, } from '@/state/config';
import { emailState, isMobileState, sessionIdSelector, userSelector, } from '@/state/session';
import { useFieldRules } from './useFieldRules';
import styles from './WelcomeFields.module.scss';
export const WelcomeFields = () => {
    const sanitizer = DOMPurification.sanitize;
    const isMobile = useRecoilValue(isMobileState);
    const termsRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const { errorMessage, fieldRules } = useFieldRules();
    const { updateContext } = useSocket();
    const sessionId = useRecoilValue(sessionIdSelector);
    const user = useRecoilValue(userSelector);
    const theme = useRecoilValue(themeSelector);
    const { headline, disclaimer, fields } = useRecoilValue(visitorInformationSelector);
    const { trackEvent } = useAnalytics();
    const setEmail = useSetRecoilState(emailState);
    const { handleLinkClick } = useHandleLinksManually();
    const { register, handleSubmit, reset, formState } = useForm();
    const { isNbg } = useRecoilValue(variantSelector);
    const isMfa = useRecoilValue(isMfaSelector);
    // Reset the loading if the sessionId changes
    useEffect(() => {
        setIsLoading(false);
    }, [sessionId]);
    useEffect(() => {
        const defaultValues = fields === null || fields === void 0 ? void 0 : fields.map(({ field }) => field).reduce((acc, value) => {
            var _a;
            return (Object.assign(Object.assign({}, acc), { [`${value}`]: user &&
                    user[value] &&
                    user[value] !== '***' &&
                    !((_a = user[value]) === null || _a === void 0 ? void 0 : _a.startsWith('Visitor '))
                    ? user[value]
                    : '' }));
        }, {});
        reset(defaultValues);
    }, [fields, reset, user]);
    const onSubmit = useCallback((formData) => {
        const { disclaimer_accepted } = formData, user = __rest(formData, ["disclaimer_accepted"]);
        trackEvent(AnalyticsEvent.WelcomeFormSubmitted);
        const language = user === null || user === void 0 ? void 0 : user.language;
        if (language && language in languageByCountryCode) {
            i18n.changeLanguage(language);
        }
        if (user === null || user === void 0 ? void 0 : user.email) {
            setEmail(user.email);
        }
        // Update the context with `visitor_form_completed:true` so that we don't
        // Show this form again
        updateContext({
            user,
            global: Object.assign({ visitor_form_completed: true }, (disclaimer_accepted ? { disclaimer_accepted: true } : {})),
        });
        setIsLoading(true);
    }, [i18n, setEmail, trackEvent, updateContext]);
    const handleTermsChange = (e) => {
        trackEvent(AnalyticsEvent.TermsAndConditionsLinkClicked, {
            value: e.target.checked,
        });
    };
    const handleTermsLinkClick = useCallback((e) => {
        trackEvent(AnalyticsEvent.TermsAndConditionsLinkClicked);
        handleLinkClick(e);
    }, [handleLinkClick, trackEvent]);
    const { dirtyFields, errors } = formState;
    return (_jsx("div", { className: cn(styles.container, styles[theme], {
            [styles.isMfa]: isMfa,
            [styles.isNbg]: isNbg,
        }), children: _jsxs("form", { className: cn({ [styles.isMobile]: isMobile }), onSubmit: handleSubmit(onSubmit), autoComplete: "off", children: [headline && _jsx("p", { className: styles.headline, children: headline }), fields === null || fields === void 0 ? void 0 : fields.map(({ field, required }) => {
                    const name = field === 'display_name' ? 'name' : field;
                    if (field === 'language') {
                        return (_jsx(LanguageSelect, { label: t(`fields.${field}`), required: required, error: errors[field] ? errorMessage(field) : null, register: register(field, Object.assign({}, fieldRules(field, required))) }, field));
                    }
                    return (_jsx(Input, { name: name, label: t(`fields.${field}`), isDirty: dirtyFields[field], required: required, error: errors[field] ? errorMessage(field) : null, register: register(field, Object.assign({}, fieldRules(field, required))) }, field));
                }), (disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.enabled) && (disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html) && (_jsxs("div", { className: cn(styles.disclaimerContainer, {
                        [styles.error]: errors.disclaimer_accepted,
                    }), children: [_jsx(Checkbox, { onChange: handleTermsChange, className: "disclaimer-check" /* TestClassName.DisclaimerCheck */, register: register('disclaimer_accepted', {
                                required: true,
                            }) }), _jsx("div", { onClick: handleTermsLinkClick, role: "presentation", ref: termsRef, className: "disclaimer-container" /* TestClassName.DisclaimerContainer */, dangerouslySetInnerHTML: {
                                __html: sanitizer(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html, { ADD_ATTR: ['target'] }),
                            } })] })), _jsx(Button, { type: "submit", fullWidth: true, isLoading: isLoading, children: t('buttons.start') })] }) }));
};
